import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scroll-reveal hook. Every marketing page uses CSS class `.tk-reveal`
 * which sets `opacity: 0` initially — content is invisible until an
 * IntersectionObserver adds the `.in` class.
 *
 * Originally the observer was scoped to LandingPage via a `ref`,
 * which meant *every other marketing page rendered with permanently
 * invisible content* — features grids, FAQs, CTAs all stayed at
 * opacity 0. Only the hero rendered because it uses `.tk-rise`
 * (pure CSS keyframe, no JS needed).
 *
 * This hook fixes that by:
 *
 *  - Querying the document directly (no ref needed) so any page can
 *    use it without restructuring
 *  - Re-running on route change (the IntersectionObserver is fresh
 *    every time, picking up new `.tk-reveal` nodes that mounted with
 *    the new page)
 *  - Already-revealed elements (`.tk-reveal.in`) are skipped so we
 *    don't waste observer slots
 *
 * Called once inside LandingNav, which is mounted on every marketing
 * page. So every page that uses LandingNav gets reveal for free.
 */
export default function useReveal() {
  const location = useLocation();
  useEffect(() => {
    // Defer to the next paint so React has flushed the new route's
    // DOM. Without this, on slow devices the observer can miss the
    // initial set of elements that mounted with the route.
    const raf = requestAnimationFrame(() => {
      const els = document.querySelectorAll('.tk-reveal:not(.in)');
      if (!els.length) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
      els.forEach((el) => io.observe(el));
    });
    return () => cancelAnimationFrame(raf);
  }, [location.pathname]);
}
